import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import Select from "react-select";

import { toastr } from "react-redux-toastr";
import { showModal } from "../../actions/modal.actions";
import { getAllDiscounts, downloadDiscounts, getPlanFeatureDiscounts } from "../../actions/accountDetailsActions/billing.actions";
import { getPlans, getAllFeatures } from "../../actions/products.action";
import { getSystemLocations } from "../../actions/admin.actions";
import Loader from "../../components/Loader";
import { getUserPermission, getProducts, getAdmin } from "../../selectors";
import { groupStyles } from "../../utils/SelectStyles";

import CamvioTable2, {
  CamvioTable2Context,
  LoadMore,
  DefaultDashboardSearch,
  defineActionButton, utils
} from "../../components/UI/CamvioTable2";

const getGnlType = (gl) => {
  const generalLedger = gl.subAccount && gl.subAccount.generalLedger;
  if (!generalLedger) return "";
  if (generalLedger.receivable) return "Receivable";
  if (generalLedger.revenue) return "Revenue";
  return "";
};

const buildGlDisplay = (selectedGeneralLedgers, systemLocations) => {
  const entries = (selectedGeneralLedgers || []).filter((gl) => gl.subAccount && gl.subAccount.generalLedger);
  if (!entries.length) return [];

  const groups = [];
  entries.forEach((gl) => {
    const location = systemLocations && systemLocations.find((l) => l.id === gl.glLocationId);
    if (!location) return;

    const { subAccount } = gl;
    const { generalLedger } = subAccount;

    const type = getGnlType(gl);
    const gnlString = `${location.name}:${generalLedger.accountNumber}.${subAccount.subAccountNumber}`;

    let group = groups.find((g) => g.type === type);
    if (!group) {
      group = { type, gnlStrings: [] };
      groups.push(group);
    }
    group.gnlStrings.push(gnlString);
  });
  return groups;
};

const DEFAULT_PAGE_SIZE = 20;

class DiscountsList extends Component {
  state = {
    isLoading: false,
    isDownloading: false,
    itemsList: [],
    planFeatureMap: {},
    itemsTotal: 0,
    noMoreResults: true,
    loadingSearch: true,
    searchTerm: "",
    statusFilter: { name: "isActive", value: "TRUE", label: "Active" },
    planFilter: null,
    featureFilter: null,
    autoApplyFilter: null,
    sortField: "",
    sortDirection: "",
    offset: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  };

  componentDidMount() {
    this.loadDiscounts(true);
    this.loadPlanFeatureDiscounts();
    this.props.getPlans();
    this.props.getAllFeatures();
    this.props.getSystemLocations();
  }

  loadPlanFeatureDiscounts = () => {
    this.props.getPlanFeatureDiscounts().then((response) => {
      const map = {};
      (response?.data || []).forEach((entry) => {
        if (entry?.discount?.id != null) map[entry.discount.id] = entry.planFeatureDiscounts || [];
      });
      this.setState({ planFeatureMap: map });
    });
  };

  getTableRequest(offsetOverride, limitOverride) {
    let filters = [];
    if (this.state.searchTerm && this.state.searchTerm.trim())
      filters.push(utils.defineFilter("keyword", this.state.searchTerm.trim(), "like"));
    if (this.state.statusFilter) {
      filters.push(utils.defineFilter(this.state.statusFilter.name, this.state.statusFilter.value));
      if (this.state.statusFilter.name === 'isActive' && this.state.statusFilter.value === 'TRUE') {
        filters.push(utils.defineFilter('isExpired', 'FALSE'));
      }
    }
    if (this.state.planFilter) {
      filters.push(utils.defineFilter("planId", this.state.planFilter.value));
    }
    if (this.state.featureFilter) {
      filters.push(utils.defineFilter("featureId", this.state.featureFilter.value));
    }
    if (this.state.autoApplyFilter) {
      filters.push(utils.defineFilter("autoApply", this.state.autoApplyFilter.value));
    }
    let sort = null;
    if (this.state.sortField && this.state.sortDirection && this.state.sortField !== 'isActive') {
      sort = utils.defineSort(this.state.sortField, this.state.sortDirection === "asc");
    }

    return utils.defineTableRequest(
      filters,
      sort,
      offsetOverride !== undefined ? offsetOverride : this.state.offset,
      limitOverride !== undefined ? limitOverride : this.state.pageSize
    );
  }

  loadDiscounts = (clearList = true) => {
    const { pageSize, offset } = this.state;
    const requestOffset = clearList ? 0 : offset;
    const tableRequest = this.getTableRequest(requestOffset, pageSize);

    this.setState({ isLoading: true, loadingSearch: clearList }, () => {
      this.props.getAllDiscounts(tableRequest).then((response) => {
        if (response && response.data && response.data.discounts) {
          const newItems = response.data.discounts;
          const total = response.data.total != null ? response.data.total : newItems.length;
          this.setState((prev) => {
            let merged = clearList ? newItems : [...prev.itemsList, ...newItems];
            if (prev.sortField === 'isActive') {
              const effectiveStatus = (item) => {
                if (item.endDate && new Date(item.endDate) < new Date()) return 2;
                if (!item.isActive) return 1;
                return 0;
              };
              const dir = prev.sortDirection === 'asc' ? 1 : -1;
              merged = [...merged].sort((a, b) => (effectiveStatus(a) - effectiveStatus(b)) * dir);
            }
            return {
              itemsList: merged,
              itemsTotal: total,
              offset: requestOffset + newItems.length,
              noMoreResults: merged.length >= total,
            };
          });
        }
        this.setState({ isLoading: false, loadingSearch: false });
      }, () => {
        this.setState({ isLoading: false, loadingSearch: false });
      });
    });
  };

  clearAndReloadList = () => {
    this.loadDiscounts(true);
  };

  showAdditional = () => {
    this.loadDiscounts(false);
  };

  handlePageSizeChange = (value) => {
    this.setState({ pageSize: parseInt(value), offset: 0 }, () => this.loadDiscounts(true));
  };

  debounceHandleInputChange = (event) => {
    this.setState({ searchTerm: event.target.value });
  };

  setStatusFilter = (value) => {
    this.setState({ statusFilter: value });
  };

  setPlanFilter = (value) => {
    this.setState({ planFilter: value });
  };

  setFeatureFilter = (value) => {
    this.setState({ featureFilter: value });
  };

  setAutoApplyFilter = (value) => {
    this.setState({ autoApplyFilter: value });
  };

  sortDiscounts = (field, dir) => {
    this.setState(
      { sortField: dir ? field : "", sortDirection: dir || "", offset: 0 },
      () => this.loadDiscounts(true)
    );
  };

  downloadTable = (fileType) => {
    const tableRequest = this.getTableRequest(0, 0);
    this.setState({ isDownloading: true });
    this.props.downloadDiscounts(fileType, tableRequest).finally(() => {
      this.setState({ isDownloading: false });
    });
  };

  onDashboardAction = (action) => {
    switch (action) {
      case "SEARCH":
        this.clearAndReloadList();
        break;
      case "ADD":
        this.props.showModal("EDIT_DISCOUNT_MODAL", {
          onSuccess: () => this.loadDiscounts(true),
        });
        break;
      case "XLS":
        this.downloadTable("XLS");
        break;
      case "CSV":
        this.downloadTable("CSV");
        break;
      default:
        break;
    }
  };

  render() {
    const { itemsList, isLoading, loadingSearch, isDownloading, noMoreResults, pageSize } = this.state;
    const { canAdd, systemLocations } = this.props;

    return (
      <Fragment>
        <CamvioTable2Context.Provider value={this.state}>
          <div className="tab-pane camvio-table-search" style={{ borderRadius: "0.5rem" }}>
            <DefaultDashboardSearch
              onAction={this.onDashboardAction}
              onInputChange={this.debounceHandleInputChange}
              disabled={isLoading || loadingSearch || isDownloading}
              inputPlaceholder="Discount name or description"
              actionButtons={{
                secondary: [defineActionButton("ADD", "New Discount", "fa-plus", !canAdd)],
              }}
              defaultShowMoreOptions={true}
            >
              <div className="search">
                <Select
                  className="extra-filter-select"
                  styles={groupStyles}
                  placeholder="All Statuses"
                  options={[
                    { name: "isActive",  value: "TRUE",  label: "Active" },
                    { name: "isActive",  value: "FALSE", label: "Inactive" },
                    { name: "isExpired", value: "TRUE",  label: "Expired" },
                  ]}
                  value={this.state.statusFilter}
                  onChange={this.setStatusFilter}
                  isClearable={true}
                  clearValue={() => this.setStatusFilter(null)}
                />
                <Select
                  className="extra-filter-select"
                  styles={groupStyles}
                  placeholder="All Plans"
                  options={(this.props.plans || []).map((p) => ({ value: p.planId, label: p.name })).sort((a, b) => a.label.localeCompare(b.label))}
                  value={this.state.planFilter}
                  onChange={this.setPlanFilter}
                  isClearable={true}
                  clearValue={() => this.setPlanFilter(null)}
                />
                <Select
                  className="extra-filter-select"
                  styles={groupStyles}
                  placeholder="All Features"
                  options={(this.props.features || []).map((f) => ({ value: f.id, label: f.name })).sort((a, b) => a.label.localeCompare(b.label))}
                  value={this.state.featureFilter}
                  onChange={this.setFeatureFilter}
                  isClearable={true}
                  clearValue={() => this.setFeatureFilter(null)}
                />
                <Select
                  className="extra-filter-select"
                  styles={groupStyles}
                  placeholder="All Auto Apply"
                  options={[
                    { value: "TRUE",  label: "On" },
                    { value: "FALSE", label: "Off" },
                  ]}
                  value={this.state.autoApplyFilter}
                  onChange={this.setAutoApplyFilter}
                  isClearable={true}
                  clearValue={() => this.setAutoApplyFilter(null)}
                />
              </div>
            </DefaultDashboardSearch>

            {isDownloading && <Loader />}

            {itemsList.length === 0 && !loadingSearch && !isDownloading && (
              <div className="container">
                <p>No data found</p>
              </div>
            )}

            {itemsList.length > 0 && !isDownloading && (
              <CamvioTable2
                headerDefs={[
                  { name: "Name",            field: "name",               sortable: true },
                  { name: "Status",          field: "isActive",           sortable: true },
                  { name: "Plans and Features", field: "",                sortable: false },
                  { name: "GL",              field: "",                   sortable: false },
                  { name: "Discount Rate",   field: "",                   sortable: false },
                  { name: "Auto Apply",      field: "autoApply",          sortable: true },
                  { name: "Action",          field: "",                   sortable: false },
                ]}
                sortFunction={this.sortDiscounts}
                sortField={this.state.sortField}
                sortDirection={this.state.sortDirection}
              >
                <tbody>
                  {itemsList.map((item) => (
                    <tr key={`discount_${item.id}`}>
                      <td>
                        {item.name}
                        <span className="td-subtitle">{item.description}</span>
                        <span className="td-subtitle">#{item.id}</span>
                      </td>
                      <td>
                        {(() => {
                          const isExpired = item.endDate && new Date(item.endDate) < new Date();
                          if (isExpired) {
                            return <span className="badge badge-secondary">Expired</span>;
                          }
                          return (
                            <span className={'badge' + (item.isActive ? ' badge-green' : ' badge-secondary')}>
                              {item.isActive ? 'Active' : 'Inactive'}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {(this.state.planFeatureMap[item.id] || []).map((pf, idx) => (
                          <span className="td-subtitle" key={pf.id != null ? pf.id : idx}>
                            {pf.planName}: {pf.featureName || 'ALL'}
                          </span>
                        ))}
                      </td>
                      <td>
                        {buildGlDisplay(item.selectedGeneralLedgers, systemLocations).map((group, idx) => (
                          <span key={idx}>
                            <span className="d-block">{group.type}</span>
                            <span className="td-subtitle">{group.gnlStrings.join(', ')}</span>
                          </span>
                        ))}
                      </td>
                      <td className="text-center">
                        {item.percentage > 0 && <span className="d-block">{`${item.percentage}%`}</span>}
                        {item.amount > 0 && <span className="d-block">{`$${item.amount.toFixed(2)}`}</span>}
                      </td>
                      <td className="text-center">
                        <span className={'badge' + (item.autoApply ? ' badge-green' : ' badge-secondary')}>
                          {item.autoApply ? 'On' : 'Off'}
                        </span>
                      </td>
                      <td>
                        <div className="tr-actions">
                          <button
                            className="btn"
                            type="button"
                            title="Apply Discount"
                            onClick={() => {
                              if (!item.isActive) {
                                toastr.warning('This discount is inactive and cannot be applied.', { timeOut: 3000, position: 'top-center' });
                                return;
                              }
                              this.props.showModal("APPLY_DISCOUNT_MODAL", {
                                value: item,
                                onSuccess: () => this.loadPlanFeatureDiscounts(),
                              });
                            }}
                          >
                            <i className="fas fa-hand-holding-usd" />
                          </button>
                          {this.props.canEdit && (
                            <button
                              className="btn"
                              type="button"
                              title="Edit"
                              onClick={() =>
                                this.props.showModal("EDIT_DISCOUNT_MODAL", {
                                  value: item,
                                  onSuccess: () => this.loadDiscounts(true),
                                })
                              }
                            >
                              <i className="fas fa-edit" />
                            </button>
                          )}
                          {this.props.canDelete && (
                            <button
                              className="btn"
                              type="button"
                              title="Delete"
                              onClick={() =>
                                this.props.showModal("DELETE_DISCOUNT_MODAL", {
                                  value: item,
                                  reloadFunction: () => this.loadDiscounts(true),
                                })
                              }
                            >
                              <i className="fas fa-trash" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CamvioTable2>
            )}
          </div>
        </CamvioTable2Context.Provider>

        {!noMoreResults && (
          <LoadMore
            disabled={isLoading || loadingSearch || isDownloading}
            onLoadMore={this.showAdditional}
            pageSize={pageSize}
            onPageSizeChange={this.handlePageSizeChange}
          />
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const canAdd = getUserPermission(state, "ADMN", "ADMN_ADD_DISCOUNT"),
    canEdit = getUserPermission(state, "ADMN", "ADMN_EDIT_DISCOUNT"),
    canDelete = getUserPermission(state, "ADMN", "ADMN_DELETE_DISCOUNT");

  return {
    canAdd,
    canEdit,
    canDelete,
    plans: getProducts(state).plans,
    features: getProducts(state).features,
    systemLocations: getAdmin(state).systemLocations,
  };
};

const mapDispatchToProps = {
  showModal,
  getAllDiscounts,
  downloadDiscounts,
  getPlanFeatureDiscounts,
  getPlans,
  getAllFeatures,
  getSystemLocations,
};

export default connect(mapStateToProps, mapDispatchToProps)(DiscountsList);